import { useContext, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import type { CustomSeriesRenderItem, EChartsOption } from "echarts";
import { RopChart } from "../../../../main/components/RopChart";
import { MainContext } from "../../../../main/context/MainContextProvider";

export type ChartDataItem = {
    name: string;
    clusterId?: string | null;
    usinaId?: number;
    items: {
        itemName: string;
        itemValue: number;
        itemMeta: number;
        color?: string;
    }[];
};

export type AvailabilityAnalysisTacticalChartProps = {
    data: ChartDataItem[];
    showLegend?: boolean;
    /** Clique na coluna (barra) do eixo X — preferir em relação a onClusterClick */
    onCategoryClick?: (item: ChartDataItem) => void;
    onClusterClick?: (clusterKey: string | null) => void;
    fitContent?: boolean;
};

export default function AvailabilityAnalysisTacticalChart({
    data,
    showLegend = true,
    onCategoryClick,
    onClusterClick,
    fitContent = false,
}: AvailabilityAnalysisTacticalChartProps) {
    const chartRef = useRef(null);
    const { theme } = useContext(MainContext);
    const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});

    const xLabels = data.map((item) => item.name);

    const seriesNames = data.length > 0
        ? data[0].items.map(item => item.itemName)
        : [];

    const visibleSeriesNames = seriesNames.filter((n) => !hiddenSeries[n]);

    const defaultColors = ["#60A5FA", "#34D399", "#A78BFA", "#F59E0B", "#EF4444"];

    const isSingleCluster = data.length === 1;
    const barWidthPercent = isSingleCluster ? "10%" : "12%";
    const barGapPercent = isSingleCluster ? "25%" : "30%";

    // Função para renderizar as linhas de meta para cada barra individual
    const renderMetaForBar = (color: string, barIndex: number, totalBars: number): CustomSeriesRenderItem => (_params, api) => {
        const xValue = api.value(0);
        const yValue = api.value(1);

        const coord = api.coord([xValue, yValue]);
        const categoryWidth = (api.size!([1, 0]) as number[])[0];

        // Calcular a largura e posição da barra individual
        const barWidthRatio = isSingleCluster ? 0.10 : 0.12;
        const barGapRatioOfBarWidth = isSingleCluster ? 0.25 : 0.30;
        const barWidth = categoryWidth * barWidthRatio;
        const barGap = barWidth * barGapRatioOfBarWidth;
        const totalBarWidth = (barWidth * totalBars) + (barGap * (totalBars - 1));
        const startX = coord[0] - totalBarWidth / 2;
        const barX = startX + (barIndex * (barWidth + barGap));

        const x1 = barX;
        const x2 = barX + barWidth;

        return {
            type: "line",
            shape: {
                x1: x1,
                y1: coord[1],
                x2: x2,
                y2: coord[1]
            },
            style: {
                stroke: color,
                lineWidth: 2,
            }
        };
    };

    const series = visibleSeriesNames.map((seriesName, index) => {
        const seriesData = data.map((categoryData) => {
            const item = categoryData.items.find((i) => i.itemName === seriesName);
            return item?.itemValue || 0;
        });

        const itemIndex = data[0].items.findIndex((i) => i.itemName === seriesName);
        const color = data[0].items[itemIndex]?.color || defaultColors[itemIndex >= 0 ? itemIndex % defaultColors.length : index % defaultColors.length];

        return {
            name: seriesName,
            data: seriesData.map((value) => ({
                value,
                itemStyle: {
                    color: color,
                    borderRadius: [4, 4, 0, 0],
                },
            })),
            type: "bar" as const,
            barWidth: barWidthPercent,
            barGap: visibleSeriesNames.length > 1 ? barGapPercent : undefined,
            label: {
                show: true,
                position: "top" as const,
                fontSize: 10,
                fontWeight: 600,
                color: color,
                formatter: (params: any) => {
                    const value = params.value;
                    return value >= 0 ? `${value.toFixed(2)}%` : "";
                },
                distance: 4,
            },
        };
    });

    const metaSeries = visibleSeriesNames.map((seriesName, seriesIndex) => {
        const metaData = data.map((categoryData) => {
            const item = categoryData.items.find((i) => i.itemName === seriesName);
            return item?.itemMeta || 0;
        });

        return {
            name: `Meta ${seriesName}`,
            type: "custom" as const,
            data: metaData,
            z: 10,
            renderItem: renderMetaForBar("#FFFFFF", seriesIndex, visibleSeriesNames.length)
        };
    });

    const option: EChartsOption = {
        backgroundColor: "transparent",
        grid: {
            left: isSingleCluster ? "30%" : "8%",
            right: isSingleCluster ? "30%" : "8%",
            bottom: "8%",
            top: "15%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            data: xLabels,
            axisLine: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            axisLabel: {
                color: theme.secondaryTextColor || "#999",
                fontSize: 12,
                margin: 12,
                interval: 0,
                rotate: xLabels.length > 4 ? 15 : 0,
            },
        },
        yAxis: {
            type: "value",
            axisLine: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            splitLine: {
                show: true,
                lineStyle: {
                    color: theme.cardBorderColor || "#374151",
                    type: "dashed",
                    opacity: 0.3,
                }
            },
            axisLabel: {
                color: theme.secondaryTextColor || "#999",
                fontSize: 11,
                margin: 8,
                formatter: "{value}%",
            }
        },
        series: [
            ...series,
            ...metaSeries
        ],
        tooltip: {
            trigger: "axis",
            backgroundColor: theme.backgroundColor || "#1a1a1a",
            borderColor: theme.cardBorderColor || "#444",
            borderWidth: 1,
            textStyle: {
                color: theme.primaryTextColor || "#fff",
                fontSize: 12,
            },
            axisPointer: {
                type: "shadow",
                shadowStyle: {
                    color: "rgba(150, 150, 150, 0.1)",
                },
            },
            formatter: (params: any) => {
                if (Array.isArray(params) && params.length > 0) {
                    const name = params[0].axisValue;
                    let tooltip = `<strong>${name}</strong><br/>`;
                    params.forEach((param: any) => {
                        tooltip += `
                            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:${param.color};margin-right:5px;"></span>
                            ${param.seriesName}: <strong>${param.value.toFixed(1)}%</strong><br/>
                        `;
                    });
                    return tooltip;
                }
                return "";
            },
        },
    };

    const handleChartClick = (params: {
        componentType?: string;
        seriesType?: string;
        dataIndex?: number;
    }) => {
        if (!onCategoryClick && !onClusterClick) return;
        if (params.componentType !== "series" || params.seriesType !== "bar") return;

        const dataIndex = params.dataIndex;
        if (dataIndex == null) return;

        const categoryData = data[dataIndex];
        if (!categoryData) return;

        if (onCategoryClick) {
            onCategoryClick(categoryData);
            return;
        }

        onClusterClick?.(categoryData.clusterId ?? categoryData.name ?? null);
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                ...(fitContent
                    ? {
                        height: "auto",
                        flexShrink: 0,
                        alignSelf: "stretch",
                        width: "100%",
                    }
                    : { height: "100%" }),
                backgroundColor: theme.cardBoxBackgroudColor,
                borderRadius: "8px",
                padding: 2,
            }}
        >

            {showLegend && seriesNames.length > 0 && (
                <Box sx={{
                    display: "flex",
                    gap: 3,
                    marginBottom: 2,
                    justifyContent: "center",
                    alignItems: "center"
                }}>
                    {seriesNames.map((name, index) => {
                        const color = data[0].items[index]?.color || defaultColors[index % defaultColors.length];
                        const isSeriesHidden = Boolean(hiddenSeries[name]);
                        return (
                            <Box
                                key={name}
                                role="button"
                                onClick={() => {
                                    setHiddenSeries((prev) => ({ ...prev, [name]: !prev[name] }));
                                }}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    cursor: "pointer",
                                    opacity: isSeriesHidden ? 0.4 : 1,
                                    userSelect: "none",
                                }}
                            >
                                <Box sx={{
                                    width: 20,
                                    height: 12,
                                    backgroundColor: color,
                                    borderRadius: "4px"
                                }} />
                                <Typography sx={{
                                    fontSize: "12px",
                                    color: theme.primaryTextColor,
                                    fontWeight: "400"
                                }}>
                                    {name}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Box
                ref={chartRef}
                sx={{
                    width: "100%",
                    height: 350,
                }}
            >
                <RopChart option={option} onClick={handleChartClick} />
            </Box>
        </Box>
    );
}
